import React, { useEffect, useRef, useState, useCallback } from "react"; 
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import host from "../../global.jsx";
import { candidateAuthHeader } from "../../utils/candidateAuth";
import { toast } from "react-toastify";
import { Clock, Award } from "lucide-react";
import { TEST_CONFIG } from '../../utils/typingTestConstants';
import {
  calculateAccuracy,
  calculateFinalScore,
  formatTime,
  getStorageKey
} from '../../utils/typingTestUtils';
import { useSelector } from "react-redux";

export default function TypingTest() {
  const navigate = useNavigate();
  const { interview_id } = useParams();

  // Refs
  const inputRef = useRef(null);
  const typingAreaRef = useRef(null);
  const typing_startTimeRef = useRef(null);
  const hasSubmittedRef = useRef(false);

  // Test content & state
  const [passage, setPassage] = useState("");
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [status, setStatus] = useState({});
  const [input, setInput] = useState("");
  const [currentLineWords, setCurrentLineWords] = useState([]);

  // Timer
  const [TOTAL_SECONDS, setTOTAL_SECONDS] = useState(60);
  const [timeLeft, setTimeLeft] = useState(60);

  // Flow control
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasResume, setHasResume] = useState(false);

  // Stats - combined into single object
  const [stats, setStats] = useState({ typed: 0, correct: 0, errors: 0 });

  const STORAGE_KEY = getStorageKey(interview_id);

  const accuracy = calculateAccuracy(stats.correct, stats.typed);

  //  const {whitelabelEnabled} = useSelector((state)=>state.miscSlice)
   const whitelabelEnabled = true

  // Memoize timer color to avoid repeated calculations
  const timerColor = timeLeft <= 10 ? "text-red-400" : timeLeft <= 30 ? "text-amber-400" : whitelabelEnabled ? "text-gray-700":"text-[#D3B4FF]";

  const saveProgress = useCallback(() => {
    if (!started || finished) return;
    const progress = {
      words,
      currentIndex,
      status,
      stats,
      savedAt: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [STORAGE_KEY, words, currentIndex, status, stats, started, finished]);

  const loadProgress = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return false;

      const progress = JSON.parse(saved);
      if (Date.now() - progress.savedAt > TEST_CONFIG.PROGRESS_EXPIRY) {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }

      setWords(progress.words);
      setCurrentIndex(progress.currentIndex);
      setStatus(progress.status);
      // Handle both old and new format
      if (progress.stats) {
        setStats(progress.stats);
      } else {
        setStats({ typed: progress.typedWords || 0, correct: progress.correctWords || 0, errors: progress.errors || 0 });
      }

      // Restore current line
      const lineStartIndex = Math.floor(progress.currentIndex / 10) * 10;
      const lineEndIndex = Math.min(lineStartIndex + 10, progress.words.length);
      setCurrentLineWords(progress.words.slice(lineStartIndex, lineEndIndex));

      return true;
    } catch (error) {
      console.error("Error loading progress:", error);
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
  }, [STORAGE_KEY]);

  const clearProgress = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, [STORAGE_KEY]);

  const loadPassage = useCallback(() => {
    if (passage) {
      const allWords = passage.split(" ").filter(Boolean);
      setWords(allWords);
      setCurrentIndex(0);
      setStatus({});
      // Load first line (8-10 words)
      setCurrentLineWords(allWords.slice(0, 10));
    }
  }, [passage]);

  // Fetch typing passage from server
  useEffect(() => {
    const fetchPassage = async () => {
      try {
        const response = await axios.get(`${host}/get_typing_passage/${interview_id}`, {
          headers: candidateAuthHeader(interview_id),
        });
        setPassage(response.data.passage);
      } catch (error) {
        console.error("Error fetching passage:", error);
        // toast.error("Failed to load typing passage. Redirecting...");
        // Redirect to test home page if passage loading fails
        setTimeout(() => {
          navigate(`/candidate/${interview_id}/testhomepage`);
        }, 2000);
      }
    };
    fetchPassage();
  }, [interview_id, navigate]);

  // Fetch typing test configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data } = await axios.get(`${host}/get_typing_timelimit`, {
          params: { interview_id },
          headers: candidateAuthHeader(interview_id),
        });

        const timeLimitInSeconds = data.typing_time_limit * 60;
        setTOTAL_SECONDS(timeLimitInSeconds);
        setTimeLeft(timeLimitInSeconds);

        if (data.typing_status === "Complete") {
          setFinished(true);
        }

        // Store start time if exists (for resume detection)
        if (data.typing_start_time) {
          typing_startTimeRef.current = new Date(data.typing_start_time);
        }
      } catch (error) {
        console.error("Error fetching config:", error);
        // toast.error("Failed to load test configuration. Redirecting...");
        setTimeout(() => navigate(`/candidate/${interview_id}/testhomepage`), 2000);
      }
    };
    fetchConfig();
  }, [interview_id, navigate]);

  // Initialize test - Load passage when it becomes available
  useEffect(() => {
    if (!passage) return; // Wait until passage is loaded

    const hasProgress = loadProgress();
    setHasResume(hasProgress);

    if (!hasProgress) {
      loadPassage();
    }
  }, [passage, loadPassage, loadProgress]);

  // Save progress periodically
  useEffect(() => {
    if (!started || finished) return;
    const interval = setInterval(saveProgress, TEST_CONFIG.AUTO_SAVE_INTERVAL);
    return () => clearInterval(interval);
  }, [started, finished, saveProgress]);

  const submitResults = useCallback(async () => {
    if (submitting) return;
    if (!typing_startTimeRef.current) {
      console.error("Cannot submit: start time ref is null");
      toast.error("Test timing error. Please contact support.");
      return;
    }

    setSubmitting(true);

    try {
      const elapsedSeconds = Math.floor((Date.now() - typing_startTimeRef.current.getTime()) / 1000);
      const timeTaken = Math.min(elapsedSeconds, TOTAL_SECONDS);
      const formatted = formatTime(timeTaken);

      // Calculate final Net WPM score (correct words per minute)
      const finalScore = calculateFinalScore(stats.correct, elapsedSeconds);

      await axios.post(`${host}/save_typing_test_result`, null, {
        headers: candidateAuthHeader(interview_id),
        params: {
          interview_id,
          wpm: finalScore,
          accuracy,
          errors: stats.errors,
          words_typed: stats.typed,
          time_taken: formatted,
          start_time: typing_startTimeRef.current.toUTCString()
        }
      });

      clearProgress();
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit results");
      setSubmitting(false);
    }
  }, [submitting, interview_id, accuracy, stats, TOTAL_SECONDS, clearProgress]);

  const finishTest = useCallback(async () => {
    if (!typing_startTimeRef.current) {
      console.error("Cannot finish test: start time ref is null");
      return;
    }

    setFinished(true);
    setStarted(false);
    setTimeLeft(0);
    inputRef.current?.blur();
    // Auto-submit will trigger from effect after finished changes
  }, []);

  const handleStartTest = useCallback(async () => {
    if (!words || words.length === 0) {
      toast.error("Test content is still loading. Please wait...");
      return;
    }

    try {
      const { data } = await axios.post(`${host}/start_typing_test`, null, {
        params: { interview_id },
        headers: candidateAuthHeader(interview_id),
      });

      const startTime = new Date(data.start_time);

      // Validate date
      if (isNaN(startTime.getTime())) {
        throw new Error("Invalid start time received from server");
      }

      typing_startTimeRef.current = startTime;

      // Calculate remaining time
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - startTime.getTime()) / 1000);

      // Check for future start time (system clock issue)
      if (elapsedSeconds < 0) {
        toast.error("System time error detected. Please check your device clock.");
        return;
      }

      const remainingSeconds = Math.max(TOTAL_SECONDS - elapsedSeconds, 0);

      // Check if test has expired (allow at least 1 second)
      if (remainingSeconds < 1) {
        toast.error("This test has already expired. Please start a new test.");
        setTimeout(() => navigate(`/candidate/${interview_id}/testhomepage`), 2000);
        return;
      }

      setTimeLeft(remainingSeconds);
      setStarted(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (error) {
      console.error("Error starting timer:", error);
      toast.error("Failed to start test timer. Redirecting...");
      setTimeout(() => navigate(`/candidate/${interview_id}/testhomepage`), 2000);
    }
  }, [words, interview_id, TOTAL_SECONDS, navigate]);

  const handleWordSubmit = useCallback(() => {
    if (finished || !started) return;

    const val = input.trim();
    const expected = words[currentIndex] ?? "";
    const isCorrect = val === expected;

    // Update stats in single call
    setStats(prev => ({
      typed: prev.typed + 1,
      correct: prev.correct + (isCorrect ? 1 : 0),
      errors: prev.errors + (isCorrect ? 0 : 1)
    }));

    setStatus(s => ({ ...s, [currentIndex]: isCorrect ? "correct" : "incorrect" }));

    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    setInput("");

    if (nextIndex >= words.length) {
      // Loop back to the start
      setCurrentIndex(0);
      setStatus({});
      setCurrentLineWords(words.slice(0, 10));
    } else {
      // Check if we need to load next line (when current word is last in line)
      const lineStartIndex = Math.floor(nextIndex / 10) * 10;
      const lineEndIndex = Math.min(lineStartIndex + 10, words.length);
      setCurrentLineWords(words.slice(lineStartIndex, lineEndIndex));
    }
  }, [finished, started, input, words, currentIndex]);

  const handleKeyDown = useCallback((e) => {
    if (!started) {
      e.preventDefault();
      return;
    }

    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      handleWordSubmit();
    } else if (e.key === "Tab") {
      e.preventDefault();
    }
  }, [started, handleWordSubmit]);

  // Timer effect (server-based timing)
  useEffect(() => {
    if (!started || finished || !typing_startTimeRef.current) return;

    const interval = setInterval(() => {
      if (!typing_startTimeRef.current) return;

      const elapsedSeconds = Math.floor((Date.now() - typing_startTimeRef.current.getTime()) / 1000);
      const left = Math.max(TOTAL_SECONDS - elapsedSeconds, 0);
      setTimeLeft(left);

      if (left <= 0) {
        finishTest();
      }
    }, TEST_CONFIG.TIMER_UPDATE_INTERVAL);

    // Always cleanup interval on unmount or dependency change
    return () => clearInterval(interval);
  }, [started, finished, TOTAL_SECONDS, finishTest]);

  // Focus management
  useEffect(() => {
    const handleDocKeyDown = (e) => {
      if (!started || finished) return;
      if (document.activeElement !== inputRef.current && e.key.length === 1) {
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleDocKeyDown);
    return () => window.removeEventListener("keydown", handleDocKeyDown);
  }, [started, finished]);

  // Auto-submit when test finishes
  useEffect(() => {
    if (!finished || submitting || hasSubmittedRef.current) return;

    hasSubmittedRef.current = true;

    const autoSubmit = async () => {
      try {
        await submitResults();

        // Wait 3 seconds to show success state, then redirect
        setTimeout(() => {
          navigate(`/candidate/${interview_id}/testhomepage`);
        }, 3000);
      } catch (error) {
        console.error("Auto-submit error:", error);
        toast.error("Failed to submit test. Please try again.");
        hasSubmittedRef.current = false; // Reset on error to allow retry
      }
    };

    autoSubmit();
  }, [finished, submitting, submitResults, navigate, interview_id]);

  // Thank You screen with auto-submit
  if (finished) {
    return (
      <div
              className={`min-h-screen ${
                whitelabelEnabled ? "bg-[#FAF9F6]" : "app-background"
              } py-12 px-4 sm:px-6 lg:px-8 animate-gradient-x`}
            >
              <div className="max-w-4xl mx-auto">
                <div className="text-center my-16 animate-fade-in">
                  <div className="flex justify-center mb-6">
                    <Award className="w-20 h-20 text-yellow-400 animate-pulse" />
                  </div>
                  <h1
                    className={`text-6xl font-semibold tracking-tight ${
                      whitelabelEnabled
                        ? "text-slate-900"
                        : "bg-gradient-to-r from-[#2960FD] to-[#FF60B6] text-transparent"
                    } bg-clip-text mb-4`}
                  >
                    Thank You!
                  </h1>
                  <h3
                    className={`text-2xl mb-6 ${
                      whitelabelEnabled ? "text-gray-700" : "text-gray-200 "
                    }`}
                  >
                    Your interview is complete.
                  </h3>
                </div>
              </div>
            </div>
    );
  }

  // Main typing screen
  return (
    <div className={`min-h-screen ${whitelabelEnabled ? 'bg-[#FAF9F6]':'app-background'} flex justify-center p-4 pt-20`}>
      <div className="max-w-4xl w-full mt-8">
        {/* Timer/Start Button - Top Right Corner */}
        <div className="flex justify-end mb-3">
          {started ? (
            <div className={`border   px-4 py-2 rounded-lg flex items-center gap-2 ${whitelabelEnabled ? "shadow-sm bg-white border-gray-300":"backdrop-blur-md shadow-lg border-[#5A4479] glass-effect"}`}>
              <Clock className={`w-4 h-4 ${timerColor}`} />
              <div>
                <div className={`text-[10px] ${whitelabelEnabled ? "text-gray-700":" text-gray-400"}`}>Time Remaining</div>
                <div className={`text-lg font-bold ${timerColor}`}>
                  {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={handleStartTest}
              className={`border-2  px-5 py-2 rounded-lg ${whitelabelEnabled ? 'bg-white border-gray-300 shadow-sm':'backdrop-blur-md glass-effect border-[#5A4479] shadow-lg hover:bg-[#D3B4FF]/20'} transition-all duration-300 ease-in-out group will-change-[background-color,transform]`}
            >
              <div className="text-[10px] text-gray-500 mb-1">
                {hasResume ? "Continue where you left off?" : "Ready to begin?"}
              </div>
              <div className={`text-base font-bold group-hover:scale-105 transition-transform duration-300 ease-out ${whitelabelEnabled ? "text-gray-700 ":'bg-gradient-to-r from-[#D3B4FF] to-[#8B5CF6] bg-clip-text text-transparent '}`}>
                {hasResume ? "RESUME TEST" : "START TEST"}
              </div>
            </button>
          )}
        </div>

        {/* Typing Area - Single Line Display */}
        <div className={` border ${whitelabelEnabled ? 'border-gray-400 shadow-sm':' border-[#5A4479] shadow-2xl glass-effect '} rounded-lg p-8 mb-3 backdrop-blur-md `}>
          {/* Progress Indicator */}
          <div className="flex justify-center mb-6">
            <div className={`text-xs ${whitelabelEnabled ? 'text-gray-700':'text-gray-400'}`}>
              Word {currentIndex + 1} of {words.length}
            </div>
          </div>

          <div
            ref={typingAreaRef}
            className="text-base leading-relaxed tracking-normal text-center h-[160px] flex items-center justify-center px-8 overflow-hidden"
          >
            <div className="flex items-center justify-center gap-2 flex-nowrap">
              {currentLineWords.map((word, i) => {
                const globalIndex = Math.floor(currentIndex / 10) * 10 + i;
                const isCurrent = globalIndex === currentIndex;
                const wordStatus = status[globalIndex];

                return (
                  <span
                    key={`${word}-${globalIndex}`}
                    data-word-index={globalIndex}
                    style={{
                      padding: isCurrent ? '0.5rem 0.75rem' : '0.5rem 0.25rem',
                    }}
                    className={`inline-block whitespace-nowrap transition-colors duration-150 ease-linear ${
                      isCurrent
                        ? `${whitelabelEnabled ? 'bg-[#1569c4] text-white':'bg-gradient-to-r shadow-lg border-[#D3B4FF] from-[#D3B4FF]/30 to-[#8B5CF6]/30'} text-white rounded border  font-semibold `
                        : wordStatus === "correct"
                        ? "text-[#069855]"
                        : wordStatus === "incorrect"
                          ? "text-red-400 line-through opacity-60"
                          : `${whitelabelEnabled ? 'text-gray-600':'text-[#CCCCCC]'}`
                      }`}
                  >
                    {word}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
          placeholder={started ? "Type each word and press Space..." : "Click START TEST to begin"}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          disabled={!started}
          className={`
            w-full border-2 rounded-lg py-2.5 px-4 text-base focus:outline-none
           disabled:opacity-50 
            disabled:cursor-not-allowed will-change-[border-color,box-shadow]
            transition-all duration-300 ease-in-out placeholder-gray-500
            focus:ring-2

            ${whitelabelEnabled
              ? "text-gray-700 shadow-sm border-gray-600 focus:border-gray-600 focus:ring-gray-600/30"
              : "text-[#CCCCCC]  glass-effect backdrop-blur-md shadow-lg  border-[#5A4479] focus:border-[#D3B4FF] focus:ring-[#D3B4FF]/30"
            }
          `}
        />

        <p className={`text-center text-[11px] ${whitelabelEnabled ? 'text-gray-600':'text-[#CCCCCC] '} mt-2`}>
          {started ? "Type accurately • Press Space after each word • Your progress is automatically saved" : "Click START TEST button above to begin your typing assessment"}
        </p>
      </div>
    </div>
  );
}
